// @core/stores/platform/PlatformStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { platformApi } from '@core/api';
import type {
  AcceptPlatformInvitationRequest,
  AddPlatformMemberRequest,
  CreateFederationPeerRequest,
  CreatePlatformInvitationRequest,
  CreatePlatformRequest,
  DataResidencyPolicyResponse,
  DeclinePlatformInvitationRequest,
  DisablePlatformProductRequest,
  EnablePlatformProductRequest,
  FederationPeerResponse,
  ListFederationPeersResponse,
  ListPlatformInvitationsResponse,
  ListPlatformMembersResponse,
  ListPlatformProductsResponse,
  Platform,
  PlatformBrandingResponse,
  PlatformInvitationResponse,
  PlatformLimitsResponse,
  PlatformMemberResponse,
  PlatformProductResponse,
  PlatformResponse,
  PlatformSettingsResponse,
  PlatformUsageResponse,
  SetPlatformProductStatusRequest,
  SetPlatformStatusRequest,
  TransferPlatformOwnershipRequest,
  UpdateDataResidencyPolicyRequest,
  UpdatePlatformBrandingRequest,
  UpdatePlatformLimitsRequest,
  UpdatePlatformMemberRoleRequest,
  UpdatePlatformProductRequest,
  UpdatePlatformRequest,
  UpdatePlatformSettingsRequest,
} from '@core/types/platform';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PlatformStore {
  // List state
  items: Platform[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Platform | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Platform>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Platform | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Platform | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Accept platform invitation
   */
  async AcceptPlatformInvitation(invitation_id: string, data: AcceptPlatformInvitationRequest): Promise<PlatformMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.AcceptPlatformInvitation(invitation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add member to platform
   */
  async AddPlatformMember(platform_id: string, data: AddPlatformMemberRequest): Promise<PlatformMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.AddPlatformMember(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel platform invitation
   */
  async CancelPlatformInvitation(platform_id: string, invitation_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.CancelPlatformInvitation(platform_id, invitation_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create federation peer
   */
  async CreateFederationPeer(platform_id: string, data: CreateFederationPeerRequest): Promise<FederationPeerResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.CreateFederationPeer(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new platform
   */
  async CreatePlatform(data: CreatePlatformRequest): Promise<Platform> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await platformApi.CreatePlatform(data);
      const item = response.platform;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Invite user to platform
   */
  async CreatePlatformInvitation(platform_id: string, data: CreatePlatformInvitationRequest): Promise<PlatformInvitationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.CreatePlatformInvitation(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Decline platform invitation
   */
  async DeclinePlatformInvitation(invitation_id: string, data: DeclinePlatformInvitationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.DeclinePlatformInvitation(invitation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete platform
   */
  async DeletePlatform(platform_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await platformApi.DeletePlatform(platform_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== platform_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === platform_id) {
          this.currentItem = null;
        }

        this.removeFromCache(platform_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Disable product on platform
   */
  async DisablePlatformProduct(platform_id: string, product_id: string, data: DisablePlatformProductRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.DisablePlatformProduct(platform_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Enable product on platform
   */
  async EnablePlatformProduct(platform_id: string, product_id: string, data: EnablePlatformProductRequest): Promise<PlatformProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.EnablePlatformProduct(platform_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get data residency policy
   */
  async GetDataResidencyPolicy(platform_id: string): Promise<DataResidencyPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetDataResidencyPolicy(platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform by ID
   */
  async GetPlatform(platform_id: string): Promise<Platform> {
    // Check cache first
    const cached = this.cache.get(platform_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await platformApi.GetPlatform(platform_id);
      const item = response.platform;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get platform branding
   */
  async GetPlatformBranding(platform_id: string): Promise<PlatformBrandingResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetPlatformBranding(platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform resource limits
   */
  async GetPlatformLimits(platform_id: string): Promise<PlatformLimitsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetPlatformLimits(platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform product configuration
   */
  async GetPlatformProduct(platform_id: string, product_id: string): Promise<PlatformProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetPlatformProduct(platform_id, product_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform settings
   */
  async GetPlatformSettings(platform_id: string): Promise<PlatformSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetPlatformSettings(platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform usage statistics
   */
  async GetPlatformUsage(platform_id: string): Promise<PlatformUsageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetPlatformUsage(platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List federation peers
   */
  async ListFederationPeers(platform_id: string, params?: PaginatedRequest): Promise<ListFederationPeersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.ListFederationPeers(platform_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List platform invitations
   */
  async ListPlatformInvitations(platform_id: string, params?: PaginatedRequest): Promise<ListPlatformInvitationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.ListPlatformInvitations(platform_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List platform members
   */
  async ListPlatformMembers(platform_id: string, params?: PaginatedRequest): Promise<ListPlatformMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.ListPlatformMembers(platform_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List products on platform
   */
  async ListPlatformProducts(platform_id: string, params?: PaginatedRequest): Promise<ListPlatformProductsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.ListPlatformProducts(platform_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List platforms
   */
  async ListPlatforms(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await platformApi.ListPlatforms(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove member from platform
   */
  async RemovePlatformMember(platform_id: string, profile_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.RemovePlatformMember(platform_id, profile_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set platform product status
   */
  async SetPlatformProductStatus(platform_id: string, product_id: string, data: SetPlatformProductStatusRequest): Promise<PlatformProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.SetPlatformProductStatus(platform_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set platform status
   */
  async SetPlatformStatus(platform_id: string, data: SetPlatformStatusRequest): Promise<PlatformResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.SetPlatformStatus(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Transfer platform ownership
   */
  async TransferPlatformOwnership(platform_id: string, data: TransferPlatformOwnershipRequest): Promise<PlatformResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.TransferPlatformOwnership(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update data residency policy
   */
  async UpdateDataResidencyPolicy(platform_id: string, data: UpdateDataResidencyPolicyRequest): Promise<DataResidencyPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdateDataResidencyPolicy(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update platform
   */
  async UpdatePlatform(platform_id: string, data: UpdatePlatformRequest): Promise<Platform> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await platformApi.UpdatePlatform(platform_id, data);
      const item = response.platform;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update platform branding
   */
  async UpdatePlatformBranding(platform_id: string, data: UpdatePlatformBrandingRequest): Promise<PlatformBrandingResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdatePlatformBranding(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update platform resource limits
   */
  async UpdatePlatformLimits(platform_id: string, data: UpdatePlatformLimitsRequest): Promise<PlatformLimitsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdatePlatformLimits(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update member role in platform
   */
  async UpdatePlatformMemberRole(platform_id: string, profile_id: string, data: UpdatePlatformMemberRoleRequest): Promise<PlatformMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdatePlatformMemberRole(platform_id, profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update platform product configuration
   */
  async UpdatePlatformProduct(platform_id: string, product_id: string, data: UpdatePlatformProductRequest): Promise<PlatformProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdatePlatformProduct(platform_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update platform settings
   */
  async UpdatePlatformSettings(platform_id: string, data: UpdatePlatformSettingsRequest): Promise<PlatformSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdatePlatformSettings(platform_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const platformStore = new PlatformStore();
